import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { Button, Checkbox, Chip, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import walletSvg from '@/enterprise/assets/wallet.svg?url';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useIncludeNewBuyerState from '@/shared/data/useIncludeNewBuyerState';

const IncludeNewBuyer = ({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const [showModal, setShowModal] = useState(false);
  const { isEnrolled } = useIncludeNewBuyerState();

  useEffectOnce(() => {
    trackEnterpriseEvent('auto-setup-offers-for-new-customers::shown', {
      referrer: 'offer-review',
      enrolled: isEnrolled,
    });
  });

  const handleCheckboxChange = (newChecked: boolean) => {
    trackEnterpriseEvent('auto-setup-offers-for-new-customers::clicked', {
      referrer: 'offer-review',
      toggleValue: newChecked,
    });
    onChange(newChecked);
  };

  return (
    <QueryBoundaries>
      <div className="rounded-md border border-transparent bg-white/10 p-2">
        {isEnrolled ? (
          <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:gap-6">
            <div>
              <div className="font-bold">{t('includeNewBuyer.title')}</div>
              <div className="mt-1 text-sm">{t('includeNewBuyer.enrolled')}</div>
            </div>
            <div className="pr-2">
              <Chip isRound label={t('core.enrolled')} type="primary" variant="outlined" />
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:gap-6">
              <div className="flex gap-4">
                <Checkbox
                  id="includeNewBuyerCheckbox"
                  className="bg-white"
                  checked={checked}
                  color="primary"
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                />
                <div className="">
                  <label htmlFor="includeNewBuyerCheckbox" className="flex cursor-pointer flex-col">
                    <div className="font-bold">{t('includeNewBuyer.title')} </div>
                    <div className="mt-1 text-sm">{t('includeNewBuyer.description')}</div>
                  </label>
                  <div className="mt-1.5 text-xs">
                    <button className="hover:underline" onClick={() => setShowModal(true)} type="button">
                      {t('helpCard.needHelp.buttonText')}
                    </button>
                  </div>
                </div>
              </div>
              <div className="md:pr-2">
                <Chip isRound label={t('core.new')} type="info" variant="filled" />
              </div>
            </div>
            <Modal onClose={() => setShowModal(false)} open={showModal} size="sm">
              <ModalTitleInfo>{t('includeNewBuyer.title')}</ModalTitleInfo>
              <ModalContent>
                <div className="flex flex-col items-center space-y-6">
                  <img src={walletSvg} alt={t('includeNewBuyer.title')} className="size-[72px]" />
                  <div>{t('includeNewBuyer.modalDescription')}</div>
                </div>
              </ModalContent>
              <ModalActions>
                <Button onClick={() => setShowModal(false)} variant="secondary">
                  {t('core.close')}
                </Button>
              </ModalActions>
            </Modal>
          </>
        )}
      </div>
    </QueryBoundaries>
  );
};

export default IncludeNewBuyer;
